/**
 * usePrefetchRatingsBatch - Batch prefetch ratings for grid display
 *
 * Problem: MovieCard uses useUserRating per card; on grids this waterfalls.
 * Solution: Single batch call seeds React Query cache so MovieCard reads instantly.
 *
 * Usage:
 *   const items = movies.map(m => ({ media_id: m.id, media_type: 'movie' }));
 *   usePrefetchRatingsBatch(items);
 *   // Now each MovieCard's useUserRating reads from pre-seeded cache
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../lib/queryKeys';
import { apiFetch } from '../lib/api';

interface MediaItem {
  media_id: number;
  media_type: 'movie' | 'tv';
}

// Response is a map: { ratings: { "movie:123": 8, "tv:456": null } }
interface BatchCheckResponse {
  ratings: Record<string, number | null>;
}

/**
 * Prefetch ratings for a batch of media items.
 * Seeds React Query cache so individual useUserRating hooks read instantly.
 *
 * @param items - Array of { media_id, media_type } (capped at 100)
 */
export function usePrefetchRatingsBatch(items: MediaItem[]): void {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  const token = session?.access_token;

  // Track last fetched set to avoid refetching same items
  const lastFetchedRef = useRef<string>('');

  useEffect(() => {
    if (!userId || !token || items.length === 0) return;

    // Cap at 100 items
    const cappedItems = items.slice(0, 100);

    // Create stable key for this item set
    const itemsKey = cappedItems
      .map((i) => `${i.media_type}-${i.media_id}`)
      .sort()
      .join(',');

    // Skip if already fetched this exact set
    if (itemsKey === lastFetchedRef.current) return;

    // Check which items are NOT already in cache
    const uncachedItems = cappedItems.filter((item) => {
      const key = queryKeys.rating.one(userId, item.media_type, item.media_id);
      return queryClient.getQueryData(key) === undefined;
    });

    // Skip if all items are already cached
    if (uncachedItems.length === 0) {
      lastFetchedRef.current = itemsKey;
      return;
    }

    // Fetch batch ratings
    const fetchBatch = async () => {
      try {
        // BUG 1 FIX: apiFetch handles JSON.stringify internally - pass object, not string
        const response = await apiFetch<BatchCheckResponse>('/ratings/batch-check', {
          token,
          method: 'POST',
          body: { items: uncachedItems },
        });

        // Seed cache for each item (including nulls for "no rating")
        // BUG 1 FIX: Cache shape must be number|null (NOT { rating: ... }) to match useUserRating
        for (const item of uncachedItems) {
          const mapKey = `${item.media_type}:${item.media_id}`;
          const rating = response.ratings[mapKey] ?? null;
          const cacheKey = queryKeys.rating.one(userId, item.media_type, item.media_id);
          queryClient.setQueryData<number | null>(cacheKey, rating);
        }

        lastFetchedRef.current = itemsKey;
      } catch (error) {
        console.error('[usePrefetchRatingsBatch] Error fetching batch:', error);
      }
    };

    fetchBatch();
  }, [items, userId, token, queryClient]);
}
